import type { AnthropicStreamEvent } from '@/types/anthropicApi';
import type { CompletionResult, StreamCallbacks, ApiCallOptions, ModelOption } from '../types';
import { openaiNonStreamingCompletion, openaiStreamCompletion } from './openai-client';

// ── Model Registry ──────────────────────────────────────────────────────────

type ModelEntry = {
  provider: 'anthropic' | 'openai';
  modelId: string;
};

const MODEL_REGISTRY: Record<ModelOption, ModelEntry> = {
  'haiku-4.5': { provider: 'anthropic', modelId: 'claude-haiku-4-5-20251001' },
  'sonnet-4.5': { provider: 'anthropic', modelId: 'claude-sonnet-4-5-20241022' },
  'sonnet-4.6': { provider: 'anthropic', modelId: 'claude-sonnet-4-6' },
  'opus-4.5': { provider: 'anthropic', modelId: 'claude-opus-4-5-20250918' },
  'opus-4.6': { provider: 'anthropic', modelId: 'claude-opus-4-6' },
  'gpt-4.1': { provider: 'openai', modelId: 'gpt-4.1' },
  'gpt-5.2': { provider: 'openai', modelId: 'gpt-5.2' },
};

export const hasAnthropicApiKey = (): boolean => !!import.meta.env.VITE_ANTHROPIC_API_KEY;

// ── Anthropic internals ─────────────────────────────────────────────────────

const ANTHROPIC_API_URL = 'https://api.anthropic.com/v1/messages';

const getAnthropicApiKey = (): string => {
  const apiKey = import.meta.env.VITE_ANTHROPIC_API_KEY;
  if (!apiKey) {
    throw new Error('VITE_ANTHROPIC_API_KEY is not configured');
  }
  return apiKey;
};

const buildAnthropicHeaders = (apiKey: string): Record<string, string> => ({
  'Content-Type': 'application/json',
  'x-api-key': apiKey,
  'anthropic-version': '2023-06-01',
  'anthropic-dangerous-direct-browser-access': 'true',
});

/**
 * Parse a Server-Sent Events line into an AnthropicStreamEvent
 */
const parseSSELine = (line: string): AnthropicStreamEvent | null => {
  if (!line.startsWith('data: ')) {
    return null;
  }

  const data = line.slice(6);
  if (data === '[DONE]') {
    return null;
  }

  try {
    return JSON.parse(data) as AnthropicStreamEvent;
  } catch {
    console.error('Failed to parse SSE data:', data);
    return null;
  }
};

const anthropicNonStreamingCompletion = async (
  modelId: string,
  options: ApiCallOptions,
): Promise<CompletionResult> => {
  const apiKey = getAnthropicApiKey();

  const messages = [{ role: 'user' as const, content: options.userPrompt }];
  const body: Record<string, unknown> = {
    model: modelId,
    max_tokens: options.maxTokens,
    stream: false,
    messages,
  };
  if (options.systemPrompt) {
    body.system = options.systemPrompt;
  }

  const response = await fetch(ANTHROPIC_API_URL, {
    method: 'POST',
    headers: buildAnthropicHeaders(apiKey),
    body: JSON.stringify(body),
    signal: options.signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Anthropic API error: ${response.status} - ${errorText}`);
  }

  const data = (await response.json()) as {
    content: Array<{ type: string; text: string }>;
    usage: { input_tokens: number; output_tokens: number };
    stop_reason: string;
  };

  const textContent = data.content.find((c) => c.type === 'text');
  if (!textContent) {
    throw new Error('No text content in response');
  }

  return {
    text: textContent.text,
    inputTokens: data.usage.input_tokens,
    outputTokens: data.usage.output_tokens,
    stopReason: data.stop_reason,
  };
};

const anthropicStreamCompletion = async (
  modelId: string,
  options: ApiCallOptions,
  callbacks: StreamCallbacks,
): Promise<void> => {
  const apiKey = getAnthropicApiKey();

  const messages = [{ role: 'user' as const, content: options.userPrompt }];
  const body: Record<string, unknown> = {
    model: modelId,
    max_tokens: options.maxTokens,
    stream: true,
    messages,
  };
  if (options.systemPrompt) {
    body.system = options.systemPrompt;
  }

  try {
    const response = await fetch(ANTHROPIC_API_URL, {
      method: 'POST',
      headers: buildAnthropicHeaders(apiKey),
      body: JSON.stringify(body),
      signal: options.signal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      callbacks.onError(new Error(`Anthropic API error: ${response.status} - ${errorText}`));
      return;
    }

    if (!response.body) {
      callbacks.onError(new Error('Response body is null'));
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      if (options.signal?.aborted) {
        reader.cancel();
        return;
      }

      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;

        const event = parseSSELine(trimmedLine);
        if (!event) continue;

        switch (event.type) {
          case 'content_block_delta':
            if (event.delta.type === 'text_delta') {
              callbacks.onTextDelta(event.delta.text);
            }
            break;

          case 'message_stop':
            break;

          case 'error':
            callbacks.onError(new Error(event.error.message));
            return;
        }
      }
    }

    // Process remaining buffer
    if (buffer.trim()) {
      const event = parseSSELine(buffer.trim());
      if (event && event.type === 'content_block_delta' && event.delta.type === 'text_delta') {
        callbacks.onTextDelta(event.delta.text);
      }
    }

    callbacks.onComplete();
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return;
    }
    callbacks.onError(error instanceof Error ? error : new Error(String(error)));
  }
};

// ── Public API (routes to correct provider) ─────────────────────────────────

/**
 * Make a non-streaming completion request, routed to the correct provider
 */
export const nonStreamingCompletion = async (
  options: ApiCallOptions,
): Promise<CompletionResult> => {
  const entry = MODEL_REGISTRY[options.model];

  if (entry.provider === 'openai') {
    return openaiNonStreamingCompletion({
      modelId: entry.modelId,
      maxTokens: options.maxTokens,
      systemPrompt: options.systemPrompt,
      userPrompt: options.userPrompt,
      signal: options.signal,
    });
  }

  return anthropicNonStreamingCompletion(entry.modelId, options);
};

/**
 * Make a streaming completion request, routed to the correct provider
 */
export const streamCompletion = async (
  options: ApiCallOptions,
  callbacks: StreamCallbacks,
): Promise<void> => {
  const entry = MODEL_REGISTRY[options.model];

  if (entry.provider === 'openai') {
    return openaiStreamCompletion(
      {
        modelId: entry.modelId,
        maxTokens: options.maxTokens,
        systemPrompt: options.systemPrompt,
        userPrompt: options.userPrompt,
        signal: options.signal,
      },
      callbacks,
    );
  }

  return anthropicStreamCompletion(entry.modelId, options, callbacks);
};

/**
 * Extract JSON from a response that may contain markdown code blocks.
 *
 * Handles several model quirks:
 * 1. Properly closed ```json ... ``` blocks
 * 2. Unclosed code blocks (```json { ... } with no closing ```)
 * 3. Prose before/after the JSON payload
 */
export const extractJsonFromResponse = (text: string): string => {
  const trimmed = text.trim();

  // Try properly closed code block first
  const closedBlockMatch = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (closedBlockMatch) {
    return closedBlockMatch[1].trim();
  }

  // Handle unclosed code block: strip the opening fence and find JSON start
  const unclosedBlockMatch = trimmed.match(/```(?:json)?\s*([\s\S]*)/);
  if (unclosedBlockMatch) {
    const afterFence = unclosedBlockMatch[1].trim();
    const jsonStart = afterFence.search(/[{[]/);
    if (jsonStart !== -1) {
      return afterFence.slice(jsonStart);
    }
  }

  // No code block — find the first { or [ in the raw text
  const jsonStart = trimmed.search(/[{[]/);
  if (jsonStart > 0) {
    return trimmed.slice(jsonStart);
  }

  return trimmed;
};
